import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './NavigationBar.css';
import { Link, NavLink } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faMobileAlt,
    faBars,
    faTimes,
    faSignOutAlt,
    faUndo,
    faFileLines,
    faUsers,
    faServer,
    faBuilding,
    faSitemap,
    faHome,
    faCertificate,
    faSliders
} from '@awesome.me/kit-e6334c76d4/icons/duotone/solid';

// import { faHouse } from '@awesome.me/kit-e6334c76d4/icons/notdog-duo/solid'
import QrCodeModal from './QrCodeModal';
import AgencySelectorModal from './AgencySelectorModal';
import { useOri } from '../context/OriContext';
import { Select, MenuItem, FormControl, InputLabel, IconButton } from '@mui/material';
import { apiFetch } from '../services/api';

const useIsSiteAdmin = () => {
    const groupsStr = sessionStorage.getItem('groups');
    if (!groupsStr) return false;
    try {
        const groups: string[] = JSON.parse(groupsStr);
        return groups.includes('site-admin');
    } catch {
        return false;
    }
};

const useIsOrgAdmin = () => {
    const groupsStr = sessionStorage.getItem('groups');
    if (!groupsStr) return false;
    try {
        const groups: string[] = JSON.parse(groupsStr);
        return groups.includes('org-admin');
    } catch {
        return false;
    }
};

const NavigationBar: React.FC = () => {
    const { logout } = useAuth();
    const isSiteAdmin = useIsSiteAdmin();
    const isOrgAdmin = useIsOrgAdmin();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isAgencySelectorOpen, setIsAgencySelectorOpen] = useState(false);
    const { selectedOri, setSelectedOri, availableOris, isLoading, defaultOri, resetToDefaultOri } = useOri();
    const firstName = sessionStorage.getItem('firstName') || 'User';

    const [qrModalOpen, setQrModalOpen] = useState(false);
    const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
    const [qrIsLoading, setQrIsLoading] = useState(false);
    const [qrError, setQrError] = useState<string | null>(null);

    const handleRemoteLogin = async () => {
        setQrDataUrl(null);
        setQrModalOpen(true);
        setQrIsLoading(true);
        setQrError(null);
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
            setQrError('Refresh token not found. Please log in again.');
            setQrIsLoading(false);
            return;
        }

        try {
            const response = await apiFetch('/api/risc/auth/qr', {
                method: 'POST',
                body: JSON.stringify({ refreshToken }),
            });

            if (!response.ok) {
                throw new Error('Failed to generate QR code.');
            }

            const data = await response.json();
            setQrDataUrl(data.qrDataUrl);
        } catch (err) {
            setQrError(err instanceof Error ? err.message : 'An unknown error occurred.');
        } finally {
            setQrIsLoading(false);
        }
    };

    return (
        <>
            <header className="navbar">
                <div className="navbar-left">
                    <div className="navbar-brand">
                        <Link to="/">
                            <img src="/logo.png" alt="RapID Logo" className="navbar-logo" aria-label="RapID Logo" />
                        </Link>
                    </div>
                    <span className="nav-link mobile-only">{selectedOri || 'None'}</span>
                    <nav className={`navbar-nav ${isMenuOpen ? 'open' : ''}`}>
                        <NavLink to="/" className="nav-link" onClick={() => setIsMenuOpen(false)}>
                            <FontAwesomeIcon icon={faHome} className="nav-icon" /> Home
                        </NavLink>
                        <NavLink to="/submissions" className="nav-link" onClick={() => setIsMenuOpen(false)}>
                            <FontAwesomeIcon icon={faFileLines} className="nav-icon" /> Submissions
                        </NavLink>
                        { (isOrgAdmin || isSiteAdmin) && (
                            <>
                                <NavLink to="/users" className="nav-link" onClick={() => setIsMenuOpen(false)}>
                                    <FontAwesomeIcon icon={faUsers} className="nav-icon" /> Users
                                </NavLink>
                                <NavLink to="/devices" className="nav-link" onClick={() => setIsMenuOpen(false)}>
                                    <FontAwesomeIcon icon={faServer} className="nav-icon" /> Devices
                                </NavLink>
                            </>
                        )}
                        { (isOrgAdmin || isSiteAdmin) && (
                            <NavLink to="/licensing" className="nav-link" onClick={() => setIsMenuOpen(false)}>
                                <FontAwesomeIcon icon={faCertificate} className="nav-icon" /> Licensing
                            </NavLink>
                        )}
                        { isSiteAdmin && (
                            <>
                                <NavLink to="/agencies" className="nav-link" onClick={() => setIsMenuOpen(false)}>
                                    <FontAwesomeIcon icon={faBuilding} className="nav-icon" /> Agencies
                                </NavLink>
                                <NavLink to="/environment" className="nav-link" onClick={() => setIsMenuOpen(false)}>
                                    <FontAwesomeIcon icon={faSliders} className="nav-icon" /> Environment
                                </NavLink>
                            </>
                        )}
                        { (isOrgAdmin || isSiteAdmin) && (
                            <>
                                <span className="nav-link mobile-only" onClick={() => { setIsAgencySelectorOpen(true); setIsMenuOpen(false); }}>
                                    <FontAwesomeIcon icon={faSitemap} className="nav-icon" /> Select Agency
                                </span>
                            </>
                        )}
                        <div className="navbar-mobile-controls">
                            <button
                                className="icon-button"
                                title="Remote Device Login"
                                onClick={() => { handleRemoteLogin(); setIsMenuOpen(false); }}
                            >
                                <FontAwesomeIcon icon={faMobileAlt} swapOpacity />
                            </button>
                            <ThemeToggle />
                            <button onClick={logout} className="logout-button" title="Logout">
                                <FontAwesomeIcon icon={faSignOutAlt} swapOpacity />
                            </button>
                        </div>
                    </nav>
                </div>
                <div className="navbar-user">
                    {(isOrgAdmin || isSiteAdmin) && !isLoading && (
                        <div className="ori-selector-container desktop-only">
                            <FormControl variant="standard" sx={{ m: 1, minWidth: 120, color: 'white' }}>
                                <InputLabel id="ori-select-label" sx={{ color: 'white' }}>Agency</InputLabel>
                                <Select
                                    labelId="ori-select-label"
                                    value={selectedOri || ''}
                                    onChange={(e) => setSelectedOri(e.target.value as string)}
                                    sx={{ color: 'white', '.MuiSvgIcon-root': { color: 'white' }  }}
                                >
                                    {availableOris.map(agency => (
                                        <MenuItem key={agency.ori} value={agency.ori} sx={{ fontSize: '0.8rem' }}>
                                            {(agency.name || '').toUpperCase()} ({agency.ori})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {selectedOri !== defaultOri && (
                                <IconButton onClick={resetToDefaultOri} title="Reset to Default ORI" size="small" sx={{ color: 'white' }}>
                                    <FontAwesomeIcon icon={faUndo} swapOpacity />
                                </IconButton>
                            )}
                        </div>
                    )}
                    <span>Welcome, {firstName}</span>
                    <button
                        className="icon-button"
                        title="Remote Device Login"
                        onClick={handleRemoteLogin}
                    >
                        <FontAwesomeIcon icon={faMobileAlt} swapOpacity />
                    </button>
                    <ThemeToggle />
                    <button onClick={logout} className="logout-button" title="Logout">
                        <FontAwesomeIcon icon={faSignOutAlt} swapOpacity />
                    </button>
                    <button className="hamburger-button" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                        <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} swapOpacity />
                    </button>
                </div>
            </header>
            <QrCodeModal
                open={qrModalOpen}
                onClose={() => setQrModalOpen(false)}
                title="Remote Device Login"
                description="Scan this QR code with your mobile device to log in. This code will expire in 5 minutes."
                qrDataUrl={qrDataUrl}
                isLoading={qrIsLoading}
                error={qrError}
            />
            {isAgencySelectorOpen && (
                <AgencySelectorModal
                    open={isAgencySelectorOpen}
                    onClose={() => setIsAgencySelectorOpen(false)}
                    agencies={availableOris}
                    onSelect={setSelectedOri}
                />
            )}
        </>
    );
};

export default NavigationBar;
